import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Coins, ArrowRight } from 'lucide-react';
import { useAuth } from '../../lib/AuthContext';
import { rewardsApi } from '../../lib/api';

/**
 * LastOrderPointsBanner — post-purchase "My Points" moment.
 * For returning logged-in customers whose latest Shopify order earned points
 * (via the orders/create webhook). Dismissed per-order via localStorage.
 */
export const LastOrderPointsBanner = () => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    rewardsApi
      .getLastOrderPoints(token)
      .then(({ data: d }) => {
        if (cancelled || !d?.has_order) return;
        if (localStorage.getItem(`points_banner_dismissed_${d.order.order_id}`)) return;
        setData(d);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [token]);

  if (!token || !data || dismissed) return null;

  const { order, available_points } = data;
  const credit = (available_points / 100).toFixed(2);

  const handleDismiss = () => {
    localStorage.setItem(`points_banner_dismissed_${order.order_id}`, '1');
    setDismissed(true);
  };

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] w-[calc(100%-2rem)] max-w-md animate-in slide-in-from-bottom-4 fade-in duration-500"
      data-testid="last-order-points-banner"
    >
      <div
        className="flex items-center gap-3 pl-4 pr-2 py-3"
        style={{
          background: 'linear-gradient(160deg, rgba(15,25,10,0.97), rgba(8,12,6,0.98))',
          border: '1px solid rgba(199,250,62,0.25)',
          borderRadius: '12px',
          boxShadow: '0 12px 40px rgba(0,0,0,0.55), 0 0 40px rgba(199,250,62,0.05)',
        }}
      >
        <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(199,250,62,0.12)' }}>
          <Coins className="w-5 h-5 text-[#C7FA3E]" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-white text-sm leading-tight" data-testid="last-order-points-text">
            Your last order earned you <span className="text-[#C7FA3E] font-bold">{order.points_awarded.toLocaleString()} pts</span>
          </p>
          <p className="text-zinc-400 text-xs mt-0.5">
            {available_points.toLocaleString()} pts stacked (= ${credit} off) —{' '}
            <Link
              to="/community/rewards"
              onClick={handleDismiss}
              className="text-[#C7FA3E] font-semibold inline-flex items-center gap-0.5 hover:underline underline-offset-2"
              data-testid="last-order-points-redeem-link"
            >
              redeem <ArrowRight className="w-3 h-3" />
            </Link>
          </p>
        </div>
        <button
          onClick={handleDismiss}
          className="w-7 h-7 flex items-center justify-center rounded-full text-zinc-500 hover:text-white hover:bg-zinc-800/70 transition-colors flex-shrink-0"
          data-testid="last-order-points-dismiss"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
